import { useEffect, useState, type ReactNode } from "react";
import { Minus, Plus, X } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import { CenteredLoader } from "@/components/common";
import { Button, Card, CardContent } from "@runcheetah/ui";

const MIN_POSTS_PER_DAY = 1;
const MAX_POSTS_PER_DAY = 7;

export function OnboardingPostingRhythmModal({
  autopilotEnabled,
  postsPerDay,
  loading,
  saving,
  onClose,
  onSave,
}: {
  autopilotEnabled: boolean;
  postsPerDay: number;
  loading: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (input: { autopilotEnabled: boolean; postsPerDay: number }) => void;
}) {
  const intl = useIntl();
  const [draftAutopilotEnabled, setDraftAutopilotEnabled] = useState(autopilotEnabled);
  const [draftPostsPerDay, setDraftPostsPerDay] = useState(postsPerDay);

  useEffect(() => {
    setDraftAutopilotEnabled(autopilotEnabled);
    setDraftPostsPerDay(postsPerDay);
  }, [autopilotEnabled, postsPerDay]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/75 p-4 backdrop-blur-sm">
      <Card className="w-full max-w-[760px] gap-0 overflow-hidden border-border/70 bg-card/95 py-0 shadow-deep">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_right_top,rgba(255,255,255,0.3),rgba(255,255,255,0)_24%),linear-gradient(180deg,#D8FF70_0%,#C8FF00_100%)] px-8 pb-5 pt-0">
          <div className="pointer-events-none absolute left-0 top-0 h-full w-28 rounded-full bg-white/20 blur-2xl" />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute right-4 top-4 rounded-full bg-black/10 hover:bg-black/15"
            onClick={onClose}
          >
            <X className="size-5 text-[#0D1F2D]" aria-hidden="true" />
            <span className="sr-only">
              {intl.formatMessage({ id: "common.close", defaultMessage: "Close" })}
            </span>
          </Button>
          <div className="relative pr-16 pt-6">
            <h2 className="text-[2rem] font-semibold tracking-[-0.04em] text-[#0D1F2D]">
              <FormattedMessage
                id="brandOnboarding.generating.postingRhythmTitle"
                defaultMessage="Pick your posting rhythm"
              />
            </h2>
          </div>
        </div>
        <CardContent className="space-y-4 p-8">
          {loading ? (
            <div className="py-12">
              <CenteredLoader
                label={intl.formatMessage({
                  id: "brandOnboarding.generating.postingRhythmLoading",
                  defaultMessage: "Loading posting settings...",
                })}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-[22px] border border-[rgba(13,31,45,.12)] bg-white p-5 shadow-[0_8px_24px_rgba(13,31,45,0.05)]">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h3 className="text-[1.05rem] font-semibold tracking-tight text-[#0D1F2D]">
                      <FormattedMessage
                        id="brandOnboarding.generating.autoCreateCampaignsTitle"
                        defaultMessage="Auto-create campaigns"
                      />
                    </h3>
                    <p className="max-w-[24rem] text-sm leading-6 text-[#6b7280]">
                      <FormattedMessage
                        id="brandOnboarding.generating.autoCreateCampaignsDescription"
                        defaultMessage="Cheetah keeps drafting fresh campaigns so your calendar never has a gap."
                      />
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-pressed={draftAutopilotEnabled}
                    onClick={() => setDraftAutopilotEnabled((current) => !current)}
                    className="relative h-10 w-20 rounded-full bg-[#0D1F2D] transition-opacity hover:opacity-95"
                  >
                    <span
                      className="absolute top-1 h-8 w-8 rounded-full bg-[#C8FF00] shadow-sm transition-[left] duration-200"
                      style={{ left: draftAutopilotEnabled ? "calc(100% - 2.25rem)" : "0.25rem" }}
                    />
                    <span className="sr-only">
                      <FormattedMessage
                        id="brandOnboarding.generating.autoCreateCampaignsToggle"
                        defaultMessage="Toggle auto-create campaigns"
                      />
                    </span>
                  </button>
                </div>
              </div>

              <div className="rounded-[22px] border border-[rgba(13,31,45,.12)] bg-white p-5 shadow-[0_8px_24px_rgba(13,31,45,0.05)]">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h3 className="text-[1.05rem] font-semibold tracking-tight text-[#0D1F2D]">
                      <FormattedMessage
                        id="brandOnboarding.generating.postsPerDayTitle"
                        defaultMessage="Posts per day"
                      />
                    </h3>
                    <p className="max-w-[18rem] text-sm leading-6 text-[#6b7280]">
                      <FormattedMessage
                        id="brandOnboarding.generating.postsPerDayDescription"
                        defaultMessage="Choose how many posts you want each day."
                      />
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StepButton
                      ariaLabel={intl.formatMessage({
                        id: "brandOnboarding.generating.decreasePostsPerDay",
                        defaultMessage: "Decrease posts per day",
                      })}
                      disabled={draftPostsPerDay <= MIN_POSTS_PER_DAY}
                      onClick={() =>
                        setDraftPostsPerDay((current) => Math.max(MIN_POSTS_PER_DAY, current - 1))
                      }
                    >
                      <Minus className="h-4 w-4" aria-hidden="true" />
                    </StepButton>
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0D1F2D] text-[2rem] font-semibold tracking-[-0.08em] text-[#C8FF00] shadow-[0_14px_30px_rgba(13,31,45,0.2)]">
                      {draftPostsPerDay}
                    </div>
                    <StepButton
                      ariaLabel={intl.formatMessage({
                        id: "brandOnboarding.generating.increasePostsPerDay",
                        defaultMessage: "Increase posts per day",
                      })}
                      disabled={draftPostsPerDay >= MAX_POSTS_PER_DAY}
                      onClick={() =>
                        setDraftPostsPerDay((current) => Math.min(MAX_POSTS_PER_DAY, current + 1))
                      }
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </StepButton>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={() =>
                    onSave({
                      autopilotEnabled: draftAutopilotEnabled,
                      postsPerDay: draftPostsPerDay,
                    })
                  }
                  disabled={saving}
                  className="h-12 rounded-full bg-[#0D1F2D] px-7 text-sm font-semibold text-white hover:bg-[#13283a]"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#C8FF00] text-[#0D1F2D]">
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <FormattedMessage
                    id="brandOnboarding.generating.postingRhythmSubmit"
                    defaultMessage="Let's go"
                  />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StepButton({
  ariaLabel,
  disabled,
  onClick,
  children,
}: {
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(13,31,45,.1)] bg-white text-[#0D1F2D] shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
